import { useEffect, useState, useRef, Fragment } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Cookies from 'universal-cookie';
import {
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    FormControl,
    Grid,
    IconButton,
    Link,
    ListItemText,
    Toolbar,
    Typography,
    Select,
    MenuItem,
    TextField,
    useMediaQuery,
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import DeleteIcon from '@material-ui/icons/Delete';
import { Edit } from '@material-ui/icons/';
import { makeStyles } from '@material-ui/core/styles';
import { Auth } from 'aws-amplify';
import { host, units_settings } from "../../../data";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
    indented: {
        marginLeft: theme.spacing(10),
    },
    content: {
        padding: theme.spacing(2),
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120
    },
    formControlLabel: {
        marginLeft: theme.spacing(2),
    },
    rootpaper: {
        width: 1000,
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
        overflowX: "auto"
    },
    dropdown: {
        display: 'flex',
        marginLeft: 'auto',
        marginRight: theme.spacing(3),
    },
    fluidtable: {
        width: '100%',
        height: '50vh',
        zIndex: 0,
        border: 0,
        WebkitFontSmoothing: 'auto',
        letterSpacing: 'normal',
        '& .MuiDataGrid-columnsContainer': {
            backgroundColor: theme.palette.type === 'light' ? theme.palette.primary.light : theme.palette.primary.dark,
            color: theme.palette.primary.contrastText
        },
        '& .MuiDataGrid-iconSeparator': {
        },
    },
    table: {
        width: '100%',
        height: '72vh',
    },
    link: {
        color: theme.palette.info.dark,
    },
}));

const FluidManagement = ({ orgInfo, devices, setDevices, fluids, setFluids }) => {
    const { t } = useTranslation();
    const matches = useMediaQuery('(max-width:600px)');
    const classes = useStyles();
    const baseurl = '/orgs/' + orgInfo.orgName;
    const [openAddFluid, setOpenAddFluid] = useState(false);
    const [deleteFluidTarget, setDeleteFluidTarget] = useState({ 'id': 0, 'name': '' });
    const [openDeleteFluid, setOpenDeleteFluid] = useState(false);
    const [editFluidTarget, setEditFluidTarget] = useState({ 'id': 0, 'name': '' });
    const [editFluidName, setEditFluidName] = useState("");
    const [openFluidEdit, setOpenFluidEdit] = useState(false);
    const rnewFluidName = useRef("");
    const [displayUnits, setDisplayUnits] = useState('US');
    const [fluidCol, setFluidCol] = useState([]);
    const [deviceCol, setDeviceCol] = useState([]);

    const fluidColumns = [
        {
            headerName: t("fluidName"),
            field: 'name',
            width: 120,
            disableClickEventBubbling: true
        },
        {
            headerName: t("monitoredTanks"),
            field: 'tankCount',
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("capactiy"),
            field: 'volume_sum_disp',
            sortComparator: (v1, v2, param1, param2) => param1.row.volume_sum - param2.row.volume_sum,
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("ullage"),
            field: 'ullage_sum_disp',
            sortComparator: (v1, v2, param1, param2) => param1.row.ullage_sum - param2.row.ullage_sum,
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: '',
            field: t('edit'),
            renderCell: renderFluidEdit,
            width: 80,
            sortable: false,
            disableClickEventBubbling: true
        },
        {
            headerName: '',
            field: t('delete'),
            renderCell: renderDeleteFluid,
            width: 80,
            sortable: false,
            disableClickEventBubbling: true
        },
    ];

    const deviceColumns = [
        {
            headerName: t("device"),
            field: 'uid',
            width: 160,
            disableClickEventBubbling: true
        },
        {
            headerName: t("branch"),
            field: 'branchName',
            renderCell: renderBranchLink,
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("site"),
            field: 'siteName',
            renderCell: renderSiteLink,
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("tank"),
            field: 'tankName',
            renderCell: renderLink,
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("fluid"),
            field: 'fluidName',
            flex: 1,
            disableClickEventBubbling: true
        },
    ];

    useEffect(() => {
        setFluidCol(fluidColumns);
        setDeviceCol(deviceColumns);
    }, []);

    const mapFunctionSetWidth = (width) => (el, index) => {
        const { flex, ...rest } = el;
        if (index !== 0) {
            return { ...rest, width }
        }
        return el;
    };

    useEffect(() => {
        if (matches) {
            setFluidCol(prevState => prevState.map(mapFunctionSetWidth(140)));
            setDeviceCol(prevState => prevState.map(mapFunctionSetWidth(130)));
        }
    }, [matches]);

    useEffect(() => {
        var cookies = new Cookies();
        var tDisplayUnits = cookies.get('DisplayUnits');

        if (undefined !== tDisplayUnits) {
            setDisplayUnits(tDisplayUnits);
        }
    }, [orgInfo]);

    useEffect(() => {
        var maxFluidNameLength = 13;
        if (fluids !== undefined) {
            for (var i = 0; i < fluids.length; i++) {
                try {
                    if (fluids[i].name.length > maxFluidNameLength) {
                        maxFluidNameLength = fluids[i].name.length;
                    }
                } catch (error) {
                }
            }
        }
        setFluidCol(prevState => prevState.map((el, index) => {
            if (index === 0) {
                return { ...el, width: maxFluidNameLength * 9 + 9 }
            }
            return el;
        }));
    }, [fluids])

    function renderLink(params) {
        if (params.row.tankName === undefined) {
            return;
        }

        return (
            <Link className={classes.link}
                component={RouterLink}
                to={baseurl.concat('/branches/').concat(params.row.branchName).concat('/sites/').concat(params.row.siteName).concat('/tanks/').concat(params.row.tankName).concat('/edit/')}>
                {params.value}
            </Link>
        );
    }

    function renderSiteLink(params) {
        if (params.row.siteName === undefined) {
            return;
        }

        return (
            <Link className={classes.link}
                component={RouterLink}
                to={baseurl.concat('/branches/').concat(params.row.branchName).concat('/sites/').concat(params.row.siteName)}>
                {params.value}
            </Link>
        );
    };

    function renderBranchLink(params) {
        if (params.row.branchName === undefined) {
            return;
        }

        return (
            <Link className={classes.link}
                component={RouterLink}
                to={baseurl.concat('/branches/').concat(params.row.branchName)}>
                {params.value}
            </Link>
        );
    };

    const handleOpenDeleteFluid = (target) => {
        setDeleteFluidTarget(target);
        setOpenDeleteFluid(true);
    };

    const handleCloseDeleteFluid = () => {
        setOpenDeleteFluid(false);
    };

    function renderDeleteFluid(params) {
        var bdisable = params.row.tankCount > 0 ? true : false;
        return (
            <IconButton edge="end" aria-label="delete" disabled={bdisable} onClick={() => handleOpenDeleteFluid(params.row)}>
                <DeleteIcon />
            </IconButton>
        );
    };

    const handleFluidDelete = () => {
        var delete_id = deleteFluidTarget.id;
        handleCloseDeleteFluid(false);

        Auth.currentAuthenticatedUser()
            .then(data => {
                var body = JSON.stringify(
                    {
                        "id": delete_id
                    }
                );

                var requestOptions = {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': data.signInUserSession.idToken.jwtToken
                    },
                    body: body
                };
                console.log(body);
                fetch(host + '/orgs/' + orgInfo.orgId + '/fluid', requestOptions)
                    .then(results => results.json())
                    .then(data => {
                        console.log(data);
                        if (data.error === "OK") {
                            setFluids(data.fluidsList);
                        }
                        else {
                            alert(data.error);
                        }
                    }).catch((error) => {
                        console.log(error)
                    })
            });
    };

    const handleOpenEditFluid = (target) => {
        setOpenFluidEdit(true);
        setEditFluidName(target.name);
        setEditFluidTarget(target);
    };

    const handleCloseFluidEdit = () => {
        setOpenFluidEdit(false);
        setEditFluidName("");
    };

    function renderFluidEdit(params) {
        return (
            <IconButton edge="end" aria-label="edit" onClick={() => handleOpenEditFluid(params.row)}>
                <Edit />
            </IconButton>
        );
    };

    const handleFluidEdit = () => {
        var newName = editFluidName;
        setOpenFluidEdit(false);

        Auth.currentAuthenticatedUser()
            .then(data => {
                var body = JSON.stringify(
                    {
                        "id": editFluidTarget.id,
                        "Name": newName,
                    }
                );

                var requestOptions = {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': data.signInUserSession.idToken.jwtToken
                    },
                    body: body
                };

                fetch(host + '/orgs/' + orgInfo.orgId + '/fluid', requestOptions)
                    .then(results => results.json())
                    .then(data => {
                        if (data.error === "OK") {
                            setFluids(data.fluidsList);

                            Auth.currentAuthenticatedUser()
                                .then(data => {
                                    var requestOptions = {
                                        method: 'GET',
                                        headers: {
                                            'Content-Type': 'application/json',
                                            'Authorization': data.signInUserSession.idToken.jwtToken
                                        },
                                    };

                                    fetch(host + '/orgs/' + orgInfo.orgId + '/devices/', requestOptions)
                                        .then(results => results.json())
                                        .then(data => {
                                            setDevices(data.deviceList);
                                            setFluids(data.fluidsList);
                                        }).catch((error) => {
                                            console.log(error)
                                        })
                                });
                        }
                        else {
                            alert(data.error);
                        }
                    }).catch((error) => {
                        console.log(error)
                    })
            });
    };

    const handleOpenAddFluid = () => {
        setOpenAddFluid(true);
        setEditFluidTarget("");
    };

    const handleCloseAddFluid = () => {
        setOpenAddFluid(false);
    };

    const handleAddFluid = () => {
        var newName = editFluidName;
        setOpenAddFluid(false);

        Auth.currentAuthenticatedUser()
            .then(data => {
                var body = JSON.stringify(
                    {
                        "Name": newName,
                    }
                );

                var requestOptions = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': data.signInUserSession.idToken.jwtToken
                    },
                    body: body
                };

                fetch(host + '/orgs/' + orgInfo.orgId + '/fluid', requestOptions)
                    .then(results => results.json())
                    .then(data => {
                        console.log(data);
                        if (data.error === "OK") {
                            setFluids(data.fluidsList);
                        } else {
                            alert(data.error);
                        }
                    }).catch((error) => {
                        console.log(error)
                    })
            });
    };

    function UpdateVolumeUnits() {
        var fluidsList = fluids;
        const vol_string = displayUnits.includes("US") ? "gal" : "L";
        if (fluids !== undefined) {
            for (var i = 0; i < fluidsList.length; i++) {
                if (displayUnits.includes("Metric")) {
                    if (fluidsList[i].VolumeUnit == 'gallons') {
                        fluidsList[i].volume_sum = Math.round((fluids[i].volume_sum) * 3.78541);
                        fluidsList[i].ullage_sum = Math.round((fluids[i].ullage_sum) * 3.78541);
                        fluidsList[i].VolumeUnit = 'liters'
                    }
                } else {
                    if (fluidsList[i].VolumeUnit == 'liters') {
                        fluidsList[i].volume_sum = Math.round((fluids[i].volume_sum) / 3.78541);
                        fluidsList[i].ullage_sum = Math.round((fluids[i].ullage_sum) / 3.78541);
                        fluidsList[i].VolumeUnit = 'gallons'
                    }
                }
                fluidsList[i].volume_sum_disp = "{vol} {unit}".replace("{vol}", fluidsList[i].volume_sum.toFixed(0)).replace("{unit}",vol_string);
                fluidsList[i].ullage_sum_disp = "{vol} {unit}".replace("{vol}", fluidsList[i].ullage_sum.toFixed(0)).replace("{unit}",vol_string);
            }

            setFluids(fluidsList);
        }
    };

    useEffect(() => {
        UpdateVolumeUnits();
    }, [fluids, displayUnits]);

    const handleSetDisplayUnits = (newSetting) => {
        var cookies = new Cookies();
        cookies.set('DisplayUnits', newSetting, { path: "/", maxAge: 1000000 });
        setDisplayUnits(newSetting);
    };

    return (
        <Fragment>
            <Grid item xs>
                <Toolbar>
                    <Typography variant='h6'>
                        {t("fluidTable")}
                    </Typography>

                    <Select className={classes.dropdown}
                        id="units-dropdown"
                        value={displayUnits}
                        onChange={(event) => handleSetDisplayUnits(event.target.value)}
                    >
                        {units_settings.map((Uval) => (
                            <MenuItem key={Uval.id} value={Uval.name}>
                                <ListItemText primary={t(Uval.label)} />
                            </MenuItem>
                        ))}
                    </Select>
                    <Button className={classes.content} onClick={handleOpenAddFluid} variant="contained" color="secondary">
                        {t('addFluid')}
                    </Button>
                </Toolbar>
            </Grid>

            <Grid item xs>
                <div className={classes.fluidtable}>
                    <DataGrid hideFooterSelectedRowCount
                        rowsPerPageOptions={[25, 50, 100]}
                        pagination
                        pageSize={100}
                        rows={fluids}
                        columns={fluidCol} />
                </div>
            </Grid>

            <Grid item xs>
                <Toolbar>
                    <Typography variant='h6'>
                        {t("deviceTable")}
                    </Typography>
                </Toolbar>
            </Grid>

            <Grid item xs>
                <div className={classes.table}>
                    <DataGrid hideFooterSelectedRowCount
                        disableColumnMenu={true}
                        rowsPerPageOptions={[25, 50, 100]}
                        pagination
                        pageSize={100}
                        rows={devices}
                        columns={deviceCol} />
                </div>
            </Grid>

            <Dialog disableBackdropClick disableEscapeKeyDown open={openFluidEdit} onClose={handleCloseFluidEdit}>
                <DialogTitle>{t("editFluid") + ' '}{editFluidTarget.name}</DialogTitle>
                <DialogContent>
                    <form className={classes.container}>
                        <FormControl className={classes.formControl}>
                            <TextField label={t("fluidName")}
                                value={editFluidName}
                                onChange={e => {
                                    const regex = /^([ a-zA-Z0-9\-]){0,40}$/i;
                                    if (regex.test(e.target.value)) { setEditFluidName(e.target.value) }
                                }}
                                inputRef={rnewFluidName} />
                        </FormControl>
                    </form>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseFluidEdit} color="primary">
                        {t("cancel")}
                    </Button>
                    <Button onClick={handleFluidEdit} variant="contained" color="primary">
                        {t("save")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog disableBackdropClick disableEscapeKeyDown open={openDeleteFluid} onClose={handleCloseDeleteFluid}>
                <DialogTitle>{t("deleteFluid") + ' '}{deleteFluidTarget.name}</DialogTitle>
                <DialogContent>
                    <Typography>{t("areYouSure")}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteFluid} variant="contained" color="secondary">
                        {t("cancel")}
                    </Button>
                    <Button onClick={handleFluidDelete} color="primary">
                        {t("delete")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog disableBackdropClick disableEscapeKeyDown open={openAddFluid} onClose={handleCloseAddFluid}>
                <DialogTitle>{t('newFluid')}</DialogTitle>
                <DialogContent>
                    <form className={classes.container}>
                        <FormControl className={classes.formControl}>
                            <TextField label={t("fluidName")}
                                value={editFluidName}
                                onChange={e => {
                                    const regex = /^([ a-zA-Z0-9\-]){0,40}$/i;
                                    if (regex.test(e.target.value)) { setEditFluidName(e.target.value) }
                                }}
                                inputRef={rnewFluidName} />
                        </FormControl>
                    </form>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseAddFluid} color="primary">
                        {t("cancel")}
                    </Button>
                    <Button onClick={handleAddFluid} variant="contained" color="primary">
                        {t("save")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

export default FluidManagement;